import * as JsSIP from 'jssip'

/**
 * 通話狀態枚舉。
 * IDLE → DIALING（發出 INVITE）→ RINGING（收到 1XX）→ CONNECTED（ACK 完成）→ ENDED（正常掛斷或失敗）
 */
export const CALL_STATE = Object.freeze({
  IDLE: 'idle',
  DIALING: 'dialing',
  RINGING: 'ringing',
  CONNECTED: 'connected',
  ENDED: 'ended'
})

/**
 * 建立一個 SIP/WebRTC 通話客戶端實例。
 * 純 JS 模組，無 Vue 依賴，可跨專案移植。
 *
 * @param {object}   config
 * @param {string}   config.wsUrl            JsSIP WebSocket 完整 URL，e.g. wss://...
 * @param {string}   config.sipDomain        SIP domain
 * @param {string}   config.sipUsername      SIP 帳號
 * @param {string}   config.guestToken       GuestToken，以 extra_headers 注入 Authorization: Bearer 至每個 SIP 請求
 * @param {Function} [config.onCallState]    (state: CALL_STATE, errorCode?: number) => void  初始可省略，可於 setCallbacks() 注入；失敗時第二參數帶入 X-Error-Code
 * @param {Function} [config.onRemoteStream] (kind: 'audio'|'video', stream: MediaStream) => void  同上
 * @param {Function} [config.onLocalStream]      (stream: MediaStream) => void  同上
 * @param {Function} [config.onLog]          (entry: { time, source, event, message, jsonData, description }) => void  僅 dev 環境傳入
 * @returns {{ init, call, hangup, toggleMute, toggleVideo, destroy, setCallbacks }}
 */
export function createSipClient (config) {
  let ua = null
  let session = null
  // rtpengine 端點學習所需的等待時間；若網路延遲較高可適當增加
  const KEYFRAME_DELAY_MS = 500

  /**
   * 處理循環參照與 Error 物件，讓任何 JsSIP 事件物件都能安全序列化為 JSON 字串。
   * JsSIP 的 newRTCSession 事件包含 RTCSession ↔ UA 互相參照，原生 JSON.stringify 會丟 cyclic 錯誤。
   */
  const stringifyLogData = (value) => {
    try {
      const ancestors = []
      const serialized = JSON.stringify(value, function replacer (key, currentValue) {
        if (typeof currentValue === 'object' && currentValue !== null) {
          while (ancestors.length > 0 && ancestors[ancestors.length - 1] !== this) {
            ancestors.pop()
          }
          if (ancestors.includes(currentValue)) return '[Circular]'
          ancestors.push(currentValue)
        }
        if (currentValue instanceof Error) {
          return { name: currentValue.name, message: currentValue.message, stack: currentValue.stack }
        }
        return currentValue
      }, 2)
      return typeof serialized === 'string' ? serialized : String(value)
    } catch (error) {
      const reason = error instanceof Error ? `${error.name}: ${error.message}` : String(error)
      return JSON.stringify({ unserializable: true, reason }, null, 2)
    }
  }

  /**
   * 記錄一筆 SIP 事件，若 config.onLog 未傳入則直接跳過。
   * @param {string} source      事件來源，e.g. 'JsSIP.UA' | 'JsSIP.RTCSession' | 'App'
   * @param {string} event       事件名稱，e.g. 'connected' | 'progress'
   * @param {string} message     人類可讀的摘要說明
   * @param {*}      data        原始事件物件或任意資料（內部以 stringifyLogData 安全序列化）
   * @param {string} description JsSIP 官方說明或補充備注
   */
  const addLog = (source, event, message, data, description) => {
    if (typeof config.onLog !== 'function') return
    config.onLog({
      time: new Date().toLocaleString('zh-TW', { hour12: false }),
      source,
      event,
      message,
      jsonData: stringifyLogData(data),
      description
    })
  }

  /**
   * 建立 JsSIP UA 並等待 WebSocket 連線完成。
   * @returns {Promise<void>}
   */
  const init = () => {
    return new Promise((resolve, reject) => {
      const socket = new JsSIP.WebSocketInterface(config.wsUrl)

      ua = new JsSIP.UA({
        sockets: [socket],
        uri: `sip:${config.sipUsername}@${config.sipDomain}`,
        extra_headers: [`Authorization: Bearer ${config.guestToken}`],
        contact_uri: `sip:${config.sipUsername}@127.0.0.1;transport=${socket.via_transport}`,
        register: false
      })

      // 持久 log 監聽器，整個 UA 生命週期內都記錄
      ua.on('connecting', (e) => addLog('JsSIP.UA', 'connecting', '正在建立 WebSocket 連線...', e, 'Fired for each transport connection attempt.'))
      ua.on('connected', (e) => addLog('JsSIP.UA', 'connected', 'WebSocket 已連線。', e, 'Fired when the transport connection is established.'))
      ua.on('disconnected', (e) => addLog('JsSIP.UA', 'disconnected', `WebSocket 已中斷。code=${e.code}, reason=${e.reason}`, e, 'Fired when the transport connection attempt (or automatic re-attempt) fails.'))
      ua.on('newRTCSession', (e) => addLog('JsSIP.UA', 'newRTCSession', `new RTCSession。originator=${e.originator}`, e, 'Fired for an incoming or outgoing session/call.'))
      ua.on('newMessage', (e) => addLog('JsSIP.UA', 'newMessage', `收到 MESSAGE。originator=${e.originator}`, e, 'Fired for an incoming or outgoing MESSAGE request.'))
      ua.on('sipEvent', (e) => addLog('JsSIP.UA', 'sipEvent', `收到 SIP Event。event=${e.event}`, e, 'Fired for an incoming out of dialog NOTIFY request.'))

      // 一次性 Promise handler：互相移除以防重複觸發
      // 初始連線失敗時呼叫 ua.stop() 阻止 JsSIP 自動重試；連線成功後若 WS 中斷則由持久 log 監聽器記錄
      const onInitConnected = () => {
        ua.off('disconnected', onInitDisconnected)
        resolve()
      }
      const onInitDisconnected = () => {
        ua.off('connected', onInitConnected)
        ua.stop()
        reject(new Error('WebSocket 連線失敗'))
      }
      ua.once('connected', onInitConnected)
      ua.once('disconnected', onInitDisconnected)

      ua.start()
    })
  }

  /**
   * 向目標 SIP 帳號發起通話。
   * @param {string} targetUsername
   */
  const call = (targetUsername) => {
    session = ua.call(`sip:${targetUsername}@${config.sipDomain}`, {
      eventHandlers: createSessionHandlers(),
      mediaConstraints: { audio: true, video: true }
    })
    config.onCallState && config.onCallState(CALL_STATE.DIALING)
  }

  const hangup = () => {
    if (session && !session.isEnded()) {
      session.terminate()
    }
  }

  /**
   * 切換麥克風靜音狀態。
   * @returns {boolean} 切換後是否靜音
   */
  const toggleMute = () => {
    if (!session) return false
    const isMuted = session.isMuted().audio
    if (isMuted) {
      session.unmute({ audio: true })
    } else {
      session.mute({ audio: true })
    }
    return !isMuted
  }

  /**
   * 切換攝影機開關狀態。
   * @returns {boolean} 切換後是否關閉視訊
   */
  const toggleVideo = () => {
    if (!session) return false
    const isVideoMuted = session.isMuted().video
    if (isVideoMuted) {
      session.unmute({ video: true })
    } else {
      session.mute({ video: true })
    }
    return !isVideoMuted
  }

  /**
   * 切換本地攝影機鏡頭（輪替所有可用裝置）。
   * 取得新 stream 後以 RTCRtpSender.replaceTrack 替換，不重新協商 SDP，通話不中斷。
   * 完成後呼叫 config.onLocalStream 讓上層更新本地視訊畫面。
   *
   * 改用 enumerateDevices + deviceId 輪替，而非依賴 facingMode；
   * 原因：部分 Android 裝置（如 Vivo Y21, Chrome）getSettings().facingMode 回傳 undefined，
   * 導致每次都拿到同一顆鏡頭。
   *
   * 切換順序：先停舊 track 再取新 track；
   * 原因：部分 Android 裝置相機硬體同時只允許一路 stream，
   * 若先 getUserMedia 再 stop 舊 track，兩路並存的瞬間會觸發 NotReadableError。
   * 若取新 track 失敗，嘗試還原至原鏡頭，避免使用者視訊完全消失。
   */
  const switchCamera = async () => {
    if (!session || !session.connection) return
    const videoSender = session.connection
      .getSenders()
      .find(s => s.track && s.track.kind === 'video')
    if (!videoSender || !videoSender.track) return

    try {
      const currentFacingMode = videoSender.track.getSettings().facingMode
      const currentDeviceId = videoSender.track.getSettings().deviceId

      // 先釋放舊 track，讓硬體空出來再給下一路 getUserMedia 使用
      const oldTrack = videoSender.track
      await videoSender.replaceTrack(null)
      oldTrack.stop()

      // facingMode 可用（iOS）：直接切換前後，讓瀏覽器選最佳鏡頭，避免在多顆後置鏡頭間輪替
      // facingMode 不可用（部分 Android）：以 deviceId 逐一輪替
      let videoConstraint
      if (currentFacingMode) {
        videoConstraint = { facingMode: { exact: currentFacingMode === 'user' ? 'environment' : 'user' } }
      } else {
        const devices = await navigator.mediaDevices.enumerateDevices()
        const videoDevices = devices.filter(d => d.kind === 'videoinput')
        if (videoDevices.length < 2) return
        const currentIndex = videoDevices.findIndex(d => d.deviceId === currentDeviceId)
        const nextDevice = videoDevices[(currentIndex + 1) % videoDevices.length]
        videoConstraint = { deviceId: { exact: nextDevice.deviceId } }
      }

      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: videoConstraint, audio: false })
        const newTrack = stream.getVideoTracks()[0]
        await videoSender.replaceTrack(newTrack)
        config.onLocalStream && config.onLocalStream(new MediaStream([newTrack]))
        addLog('App', 'switchCamera', `鏡頭已切換。facingMode=${newTrack.getSettings().facingMode || 'unknown'}`, null, '')
      } catch (acquireError) {
        // 取得新鏡頭失敗，嘗試還原至原鏡頭
        addLog('App', 'switchCamera:failed', '取得新鏡頭失敗，嘗試還原原鏡頭。', acquireError, '')
        try {
          const fallbackStream = await navigator.mediaDevices.getUserMedia({
            video: currentDeviceId ? { deviceId: { exact: currentDeviceId } } : true,
            audio: false
          })
          const fallbackTrack = fallbackStream.getVideoTracks()[0]
          await videoSender.replaceTrack(fallbackTrack)
          config.onLocalStream && config.onLocalStream(new MediaStream([fallbackTrack]))
        } catch (restoreError) {
          addLog('App', 'switchCamera:restore:failed', '還原原鏡頭失敗，視訊已暫停。', restoreError, '')
        }
      }
    } catch (error) {
      addLog('App', 'switchCamera:failed', '切換鏡頭失敗。', error, '')
    }
  }

  const destroy = () => {
    hangup()
    if (ua) {
      ua.stop()
      ua = null
    }
  }

  /**
   * rtpengine 端點學習機制補償：
   * rtpengine 需收到雙方第一個 RTP 封包後才能得知對方 IP/Port 並開始轉送。
   * 遠端 video track unmute（開始送 RTP）後延遲 500ms，再主動向瀏覽器請求 key frame，
   * 確保硬體端可正確解碼畫面。
   */
  const generateKeyFrame = async () => {
    const peerConnection = session && session.connection
    if (!peerConnection) return

    const videoSender = peerConnection
      .getSenders()
      .find(s => s.track && s.track.kind === 'video' && s.track.readyState === 'live')
    if (!videoSender) return

    try {
      await new Promise(resolve => setTimeout(resolve, KEYFRAME_DELAY_MS))
      const params = videoSender.getParameters()
      const count = Math.max((params.encodings && params.encodings.length) || 0, 1)
      await videoSender.setParameters(params, {
        encodingOptions: Array.from({ length: count }, () => ({ keyFrame: true }))
      })
      addLog('App', 'generateKeyFrame', '已透過 RTCRtpSender.setParameters(..., { encodingOptions }) 觸發 key frame。', { encodingCount: count }, '')
    } catch (error) {
      addLog('App', 'generateKeyFrame:failed', 'key frame 請求失敗。', error, '瀏覽器可能尚未支援相關 API，或 sender 狀態已改變。')
    }
  }

  /**
   * 從 JsSIP 事件物件中擷取自訂 SIP header X-Error-Code 的數值。
   * ended 與 failed 事件共用。
   * @param {object} event JsSIP RTCSession 事件物件
   * @returns {number|null}
   */
  const extractErrorCode = (event) => {
    const rawCode = event.message?.headers?.['X-Error-Code']?.[0]?.raw
    return rawCode ? parseInt(rawCode, 10) : null
  }

  /**
   * 建立 RTCSession 的事件處理器映射，傳入 ua.call() 的 eventHandlers 選項。
   * 每個 handler 除了驅動 callState 狀態機之外，也會呼叫 addLog 記錄事件供 dev 環境偵錯。
   * @returns {object} JsSIP RTCSession eventHandlers 物件
   */
  const createSessionHandlers = () => ({
    peerconnection (event) {
      addLog('JsSIP.RTCSession', 'peerconnection', 'PeerConnection 已建立。', event, 'Fired once the underlying RTCPeerConnection is created.')

      // MediaStream.getTracks() 的回傳順序不受規格保證，iOS WebKit 可能回傳 video、audio，
      // 而 JsSIP 會依該順序呼叫 addTrack()，使初始 offer 產生 m=video 在 m=audio 前的 SDP。
      // 被叫端 Linphone 無法協商此順序並回覆 SIP 488，因此在 JsSIP 加入 track 前先依序建立
      // audio、video transceiver；後續 addTrack() 會依媒體類型重用既有 transceiver，
      // 使初始 offer 的 m-line 穩定維持 audio-first，且不需要直接修改 SDP 字串。
      //
      // 注意：本修法依賴 JsSIP 的內部實作（peerconnection 事件先於 addTrack() 觸發，
      // 且 JsSIP 以 addTrack() 而非 addTransceiver() 加入媒體）。升級 JsSIP 後若內部行為
      // 改變，此修法會靜默失效，症狀為 iOS 端間歇性 488 復發；升級後須以 sdp 事件驗證
      // local offer 的 m=audio 仍先於 m=video。
      //
      // 本區塊僅適用撥出（outgoing）方向；call() 固定 mediaConstraints 為
      // { audio: true, video: true }，故固定建立 audio + video 兩個 transceiver。
      event.peerconnection.addTransceiver('audio', { direction: 'sendrecv' })
      event.peerconnection.addTransceiver('video', { direction: 'sendrecv' })

      event.peerconnection.ontrack = (trackEvent) => {
        const stream = trackEvent.streams[0]
        if (trackEvent.track.kind === 'audio') {
          config.onRemoteStream && config.onRemoteStream('audio', stream)
        } else if (trackEvent.track.kind === 'video') {
          config.onRemoteStream && config.onRemoteStream('video', stream)
          // onunmute 僅保留用於 key frame 補償；onRemoteVideoState 改由 getStats() 輪詢偵測
          trackEvent.track.onunmute = () => {
            addLog('JsSIP.RTCSession', 'video track:unmute', '遠端 video 軌道已解除靜音。', null, '當遠端軌道開始傳送媒體時觸發，視為畫面開始恢復的時機點。')
            generateKeyFrame()
          }
        }
      }
    },
    connecting (event) {
      addLog('JsSIP.RTCSession', 'connecting', '正在建立通話媒體連線。', event, 'Fired after the local media stream is added into RTCSession and before ICE gathering starts.')
    },
    sending (event) {
      addLog('JsSIP.RTCSession', 'sending', '即將送出 INVITE。', event, 'Fired just before the initial INVITE is sent.')
    },
    progress (event) {
      addLog('JsSIP.RTCSession', 'progress', '對方響鈴中。', event, 'Fired when receiving or generating a 1XX SIP class response (>100).')
      config.onCallState && config.onCallState(CALL_STATE.RINGING)
    },
    accepted (event) {
      addLog('JsSIP.RTCSession', 'accepted', '對方已接受通話。', event, 'Fired when the call is accepted (2XX received/sent).')
    },
    confirmed (event) {
      addLog('JsSIP.RTCSession', 'confirmed', '通話已建立。', event, 'Fired when the call is confirmed (ACK received/sent).')
      config.onCallState && config.onCallState(CALL_STATE.CONNECTED)
      if (session && session.connection) {
        const localTracks = session.connection
          .getSenders()
          .map(s => s.track)
          .filter(t => t && t.readyState !== 'ended')
        if (localTracks.length > 0) {
          config.onLocalStream && config.onLocalStream(new MediaStream(localTracks))
        }
      }
    },
    ended (event) {
      addLog('JsSIP.RTCSession', 'ended', `通話已結束。cause=${event.cause}`, event, 'Fired when an established call ends.')
      const errorCode = extractErrorCode(event)
      config.onCallState && config.onCallState(CALL_STATE.ENDED, errorCode, event.cause)
    },
    failed (event) {
      addLog('JsSIP.RTCSession', 'failed', `通話失敗。cause=${event.cause}`, event, 'Fired when the session was unable to establish.')
      const errorCode = extractErrorCode(event)
      config.onCallState && config.onCallState(CALL_STATE.ENDED, errorCode, event.cause)
    },
    muted (event) {
      addLog('JsSIP.RTCSession', 'muted', '本地媒體已靜音。', event, 'Fired when the local media is muted.')
    },
    unmuted (event) {
      addLog('JsSIP.RTCSession', 'unmuted', '本地媒體已解除靜音。', event, 'Fired when the local media is unmuted.')
    },
    reinvite (event) {
      addLog('JsSIP.RTCSession', 'reinvite', '收到 in-dialog reINVITE。', event, 'Fired when an in-dialog reINVITE is received.')
    },
    update (event) {
      addLog('JsSIP.RTCSession', 'update', '收到 in-dialog UPDATE。', event, 'Fired when an in-dialog UPDATE is received.')
    },
    sdp (event) {
      addLog('JsSIP.RTCSession', 'sdp', `SDP 事件。originator=${event.originator}, type=${event.type}`, event, 'Fired before passing a remote SDP to the RTC engine.')
    },
    icecandidate (event) {
      addLog('JsSIP.RTCSession', 'icecandidate', 'ICE candidate 已產生。', event, 'Fired for each gathered local ICE candidate.')
    },
    getusermediafailed (event) {
      addLog('JsSIP.RTCSession', 'getusermediafailed', 'JsSIP 內部媒體取得失敗。', event, 'Fired when an internal call to getUserMedia() fails.')
    },
    'peerconnection:createofferfailed' (event) {
      addLog('JsSIP.RTCSession', 'peerconnection:createofferfailed', '建立 SDP offer 失敗。', event, 'Fired when an internal call to createOffer() fails.')
    },
    'peerconnection:createanswerfailed' (event) {
      addLog('JsSIP.RTCSession', 'peerconnection:createanswerfailed', '建立 SDP answer 失敗。', event, 'Fired when an internal call to createAnswer() fails.')
    },
    'peerconnection:setlocaldescriptionfailed' (event) {
      addLog('JsSIP.RTCSession', 'peerconnection:setlocaldescriptionfailed', '本地 SDP 設定失敗。', event, 'Fired when an internal call to setLocalDescription() fails.')
    },
    'peerconnection:setremotedescriptionfailed' (event) {
      addLog('JsSIP.RTCSession', 'peerconnection:setremotedescriptionfailed', '遠端 SDP 設定失敗。', event, 'Fired when an internal call to RTCPeerConnection.setRemoteDescription() fails.')
    }
  })

  /**
   * 動態更新媒體與狀態 callback，供 CallingDialog mount 後注入；傳入空物件可清除所有 callback。
   * CalleesPage 持有 sipClient 並提前 init()，CallingDialog mount 時呼叫 setCallbacks 再 call()。
   * @param {object}   callbacks
   * @param {Function} [callbacks.onCallState]
   * @param {Function} [callbacks.onRemoteStream]
   * @param {Function} [callbacks.onLocalStream]
   */
  const setCallbacks = (callbacks) => {
    config.onCallState = callbacks.onCallState || null
    config.onRemoteStream = callbacks.onRemoteStream || null
    config.onLocalStream = callbacks.onLocalStream || null
  }

  return { init, call, hangup, toggleMute, toggleVideo, switchCamera, destroy, setCallbacks }
}
